import React, { useState } from 'react';
import { X, Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { lerToken } from '../services/sessao';

interface AlterarSenhaModalProps {
  onClose: () => void;
}

export default function AlterarSenhaModal({ onClose }: AlterarSenhaModalProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Avaliação visual da força da nova senha
  const strength = (() => {
    if (!newPassword) return { level: 0, label: '', color: '#ddd' };
    let score = 0;
    if (newPassword.length >= 8) score++;
    if (newPassword.length >= 12) score++;
    if (/[A-Z]/.test(newPassword)) score++;
    if (/[a-z]/.test(newPassword)) score++;
    if (/[0-9]/.test(newPassword)) score++;
    if (/[^A-Za-z0-9]/.test(newPassword)) score++;
    if (score <= 2) return { level: 1, label: 'Fraca', color: '#e53935' };
    if (score <= 4) return { level: 2, label: 'Média', color: '#fb8c00' };
    return { level: 3, label: 'Forte', color: '#43a047' };
  })();

  const validate = (): string | null => {
    if (!currentPassword) return 'Informe a senha atual';
    if (!newPassword) return 'Informe a nova senha';
    if (newPassword.length < 8) return 'Nova senha deve ter ao menos 8 caracteres';
    if (!/[A-Za-z]/.test(newPassword) || !/[0-9]/.test(newPassword))
      return 'Nova senha deve conter letras e números';
    if (newPassword === currentPassword) return 'Nova senha deve ser diferente da atual';
    if (newPassword !== confirmPassword) return 'Confirmação não confere';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    setLoading(true);
    try {
      const token = lerToken('saas') || '';
      const resp = await fetch('/api/users/me/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.message || `Erro ${resp.status}`);
      }
      setSuccess(true);
      setTimeout(() => onClose(), 1800);
    } catch (err: any) {
      setError(err.message || 'Falha ao alterar senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 12, padding: 28, width: '100%', maxWidth: 480,
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 40, height: 40, background: 'rgba(255,64,129,0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ff4081' }}>
              <Lock size={20} />
            </div>
            <h3 style={{ margin: 0, color: '#333' }}>Alterar Senha</h3>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#666' }}>
            <X size={22} />
          </button>
        </div>

        {success ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <CheckCircle size={56} color="#43a047" />
            <h4 style={{ margin: '12px 0 4px 0', color: '#333' }}>Senha alterada com sucesso!</h4>
            <p style={{ color: '#666', fontSize: 14 }}>Use sua nova senha no próximo login.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <PasswordField label="Senha atual *" value={currentPassword} onChange={setCurrentPassword} show={showCurrent} setShow={setShowCurrent} />
            <PasswordField label="Nova senha *" value={newPassword} onChange={setNewPassword} show={showNew} setShow={setShowNew} />

            {newPassword && (
              <div style={{ margin: '4px 0 12px 0' }}>
                <div style={{ height: 6, background: '#eee', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${(strength.level / 3) * 100}%`, background: strength.color, transition: 'all 0.3s' }} />
                </div>
                <div style={{ fontSize: 11, color: strength.color, marginTop: 4, fontWeight: 600 }}>{strength.label}</div>
              </div>
            )}

            <PasswordField label="Confirme a nova senha *" value={confirmPassword} onChange={setConfirmPassword} show={showConfirm} setShow={setShowConfirm} />

            <div style={{ background: '#f8f8f8', borderRadius: 6, padding: '10px 12px', marginBottom: 16, fontSize: 12, color: '#666' }}>
              <strong>Requisitos:</strong> mínimo 8 caracteres, ao menos 1 letra e 1 número, diferente da atual.
            </div>

            {error && (
              <div style={{ background: '#ffebee', border: '1px solid #ffcdd2', color: '#c62828', borderRadius: 6, padding: '10px 12px', marginBottom: 12, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertCircle size={16} /> {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={onClose} disabled={loading}
                style={{ padding: '10px 18px', border: '1px solid #ddd', background: '#fff', borderRadius: 6, cursor: 'pointer' }}>
                Cancelar
              </button>
              <button type="submit" disabled={loading}
                style={{ padding: '10px 18px', border: 'none', background: loading ? '#ccc' : '#ff4081', color: '#fff', borderRadius: 6, cursor: loading ? 'wait' : 'pointer', fontWeight: 600 }}>
                {loading ? 'Alterando...' : 'Alterar Senha'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function PasswordField({ label, value, onChange, show, setShow }: {
  label: string; value: string; onChange: (v: string) => void; show: boolean; setShow: (b: boolean) => void;
}) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: '#333' }}>{label}</label>
      <div style={{ position: 'relative' }}>
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: '100%', padding: '10px 38px 10px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
          autoComplete="off"
        />
        <button type="button" onClick={() => setShow(!show)}
          style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', color: '#888', padding: 4 }}>
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}
